import { CaretDownIcon } from '@wonderschool/common-base-ui';
import { getAuth } from 'firebase/auth';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import Avatar from 'react-avatar';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
// eslint-disable-next-line no-restricted-imports
import { Dropdown } from 'semantic-ui-react';
import { routes } from '../../config/routes';
import { formatFullName } from '../../helpers/utils';

const NOVA_URL = import.meta.env.VITE_WS_MARKETPLACE_APP_URL || '';

const caretIcon = <CaretDownIcon className="ml-1 inline size-3.5 align-middle" />;

const UserProfileMenu = ({ user, viewport = 'desktop', onClick = undefined }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [multiRoleEnabled, setMultiRoleEnabled] = useState(false);

  // Check if multi-role is enabled for this user by calling the memberships API.
  // If it returns data, the flag is on and we show the "My Roles" menu item.
  useEffect(() => {
    if (!user?.email) return;
    let cancelled = false;

    async function checkMultiRole() {
      try {
        const currentUser = getAuth().currentUser;
        if (!currentUser) return;
        const token = await currentUser.getIdToken();
        const res = await fetch(`${NOVA_URL}/firebase-api/memberships`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.ok) {
          setMultiRoleEnabled(true);
        }
      } catch {
        // Silently fail — menu item just doesn't show
      }
    }

    checkMultiRole();
    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  if (!user || !user.email) return null;

  const handleChange = (_, { value }) => {
    switch (value) {
      case 'profile':
        if (onClick) {
          onClick();
        }
        navigate(routes.profile);
        break;
      case 'settings':
        navigate(routes.settings);
        break;
      case 'my-roles':
        navigate('/my-roles');
        break;
      case 'sign-out':
        navigate(routes.signout);
        break;
      default:
        break;
    }
  };

  const trigger = (
    <span>
      <Avatar
        name={formatFullName(user, true)}
        round
        size={35}
        className="left floated"
        src={user.photoURL}
        data-testid="avatar"
      />
      <span>
        &nbsp;&nbsp;
        {user.displayName || user.email}
      </span>
    </span>
  );

  const options = [
    {
      key: 'profile',
      value: 'profile',
      text: t('navigation.userMenu.profileAction'),
    },
    ...(multiRoleEnabled
      ? [
          {
            key: 'my-roles',
            value: 'my-roles',
            text: t('navigation.userMenu.rolesAction'),
          },
        ]
      : []),
    {
      key: 'sign-out',
      value: 'sign-out',
      text: t('navigation.userMenu.signOutAction'),
    },
  ];

  const userMenu =
    viewport === 'mobile' ? (
      <Dropdown
        trigger={trigger}
        icon={caretIcon}
        options={options}
        selectOnBlur={false}
        selectOnNavigation={false}
        onChange={handleChange}
      />
    ) : (
      <Dropdown
        item
        trigger={trigger}
        icon={caretIcon}
        options={options}
        selectOnBlur={false}
        selectOnNavigation={false}
        onChange={handleChange}
        value="" // never select a menu item
        data-testid="user-profile"
      />
    );

  return userMenu;
};

UserProfileMenu.propTypes = {
  user: PropTypes.shape({
    uid: PropTypes.string,
  }),
  onClick: PropTypes.func,
};
const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps, {})(UserProfileMenu);
